import React, { useState } from 'react';
import image1 from '../../../Assets/Product/newProduct.png'

export default function NewProduct() {
  const [activeTab, setActiveTab] = useState('b2b');
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqItems = [
    {
      question: 'How does Datoula generate lead intelligence?',
      answer: "Datoula's AI engine aggregates and analyses publicly available data from LinkedIn, Twitter/X, company websites, news articles, and professional directories. It uses advanced natural language processing to synthesise this data into structured, actionable customer insight profiles — delivered in under 30 seconds.",
    },
    {
      question: 'Does Datoula use private or personal data?',
      answer: 'No. Datoula exclusively analyses publicly available data — the kind of information a diligent sales rep would find through their own manual research. We do not access, store, or process any private, gated, or personal data beyond what is openly published online.',
    },
    {
      question: 'Which CRMs does Datoula integrate with?',
      answer: 'Datoula integrates natively with Salesforce, HubSpot, Pipedrive, and Zoho CRM on Growth and Enterprise plans. We also offer a universal CSV export and REST API for custom integrations on the Enterprise plan.',
    },
    {
      question: 'How long does it take to get AI insights on a lead?',
      answer: 'In most cases, AI lead insights are generated within 15 to 30 seconds of importing a lead. Bulk imports for larger lists typically complete within a few minutes, depending on volume.',
    },
    {
      question: 'Is Datoula GDPR and data privacy compliant?',
      answer: 'Yes. Datoula is fully GDPR compliant. We process only publicly available professional data, maintain strict data handling policies, and provide users with clear controls over how data is managed within the platform. Our privacy practices are audited regularly.',
    },
    {
      question: 'Can I try Datoula before committing?',
      answer: 'Yes. All Datoula plans include a 14-day free trial with full access to all features. No credit card is required to start. You can upgrade, downgrade, or cancel at any time — no questions asked.',
    },
    {
      question: 'Is Datoula suitable for startups and small teams?',
      answer: "Absolutely. Datoula's Starter plan is designed specifically for individual reps and small sales teams. Many of our customers are early-stage startups using Datoula to compete with the research capabilities of much larger organisations — without the headcount.",
    },
  ];

  const industryData = {
    b2b: {
      title: 'Enterprise & Mid-Market B2B Sales',
      description: 'B2B sales cycles are long, complex, and multi-stakeholder. Walking into a discovery call or executive demo without knowing your stakeholders\' backgrounds, priorities, and current pain points is a costly mistake Datoula eliminates entirely. Your reps arrive fully briefed — on the person, the company, and the context — so every conversation drives the deal forward rather than restarting it.',
      wins: [
        'Understand stakeholder priorities before discovery calls and demos',
        'Know which objections are likely before they\'re raised',
        'Identify buying committee members and their influence levels',
        'Reduce deal cycles by entering each meeting already aligned',
        'Arm your reps with context that builds trust from sentence one',
      ],
      metrics: [
        { label: 'Response rates', value: '3× higher', width: 85 },
        { label: 'Research time saved', value: '67% less', width: 67 },
        { label: 'Pipeline increase', value: '+40%', width: 40 },
        { label: 'Deal confidence', value: '91%', width: 91 },
      ],
    },
    sdr: {
      title: 'SDR & Business Development Teams',
      description: 'SDRs live and die by their reply rates. Generic sequences don\'t cut it when every prospect gets dozens of cold messages a day. Datoula replaces templated spray-and-pray with intelligent, contextual outreach that references something real — a recent post, a career move, a shared connection — turning cold prospects into warm conversations at scale, without sacrificing speed.',
      wins: [
        'Personalise cold outreach at scale — 50 prospects/day, same quality as 5',
        'Boost reply rates from the industry average 2–3% to 10%+',
        'Book more qualified meetings with less manual research',
        'Replace generic sequences with contextual messaging that resonates',
        'Help junior reps perform like seasoned account executives',
      ],
      metrics: [
        { label: 'Cold reply rates', value: '~11%', width: 78 },
        { label: 'Meeting book rate', value: '3× higher', width: 65 },
        { label: 'Research time', value: '30s avg', width: 33 },
        { label: 'Daily prospect volume', value: '10× more', width: 90 },
      ],
    },
    saas: {
      title: 'SaaS Startups',
      description: 'Early-stage SaaS founders and small sales teams rarely have the budget for large research operations. Datoula levels the playing field — delivering enterprise-grade lead intelligence from day one, without the headcount. Whether you\'re a solo founder doing outbound yourself or a 3-person sales team competing against established players, Datoula gives you the intelligence edge to punch well above your weight.',
      wins: [
        'Compete with the research capabilities of companies 10× your size',
        'Founders can personalise outbound without hiring a research team',
        'Scale prospecting without scaling overhead',
        'Starter plan designed specifically for small teams and individuals',
        'Start free — no credit card, full feature access for 14 days',
      ],
      metrics: [
        { label: 'Booked meetings', value: '3× more', width: 80 },
        { label: 'Onboarding time', value: '< 1 hour', width: 20 },
        { label: 'Conversion uplift', value: '+55%', width: 55 },
        { label: 'Cost vs hiring researcher', value: '~95% cheaper', width: 95 },
      ],
    },
    abm: {
      title: 'Account-Based Marketing Teams',
      description: 'ABM campaigns live or die on the quality of account intelligence. Generic firmographic data — industry, size, revenue — tells you almost nothing about whether a company is actually in-market right now. Datoula goes far deeper, giving ABM teams the AI insights they need to craft hyper-targeted campaigns, identify the right stakeholders, and align marketing and sales on the highest-priority accounts with shared, real-time intelligence.',
      wins: [
        'Build richer account intelligence beyond basic firmographics',
        'Identify in-market accounts before competitors do',
        'Align sales and marketing on the same lead intelligence layer',
        'Craft campaigns that speak to specific stakeholder priorities',
        'Surface buying signals to time campaign delivery perfectly',
      ],
      metrics: [
        { label: 'Campaign relevance score', value: '+88%', width: 88 },
        { label: 'Sales-marketing alignment', value: '+75%', width: 75 },
        { label: 'Account identification', value: '3× faster', width: 60 },
      ],
    },
    recruit: {
      title: 'Recruitment & Staffing Agencies',
      description: 'Great recruiters build relationships — and relationships are built on understanding. Before your first conversation with a candidate or client, Datoula surfaces their career trajectory, professional motivations, communication preferences, and likely priorities. This means less time on awkward discovery calls and more time building the human connection that wins placements — and referrals.',
      wins: [
        'Understand candidate career motivations before the first call',
        'Research hiring manager priorities instantly before client meetings',
        'Tailor candidate pitches to what each hiring manager actually cares about',
        'Reduce time-to-placement by qualifying faster with better intelligence',
        'Build deeper client relationships with contextual, relevant conversations',
      ],
      metrics: [
        { label: 'Candidate match quality', value: '+82%', width: 82 },
        { label: 'Client call quality', value: '+70%', width: 70 },
        { label: 'Time-to-placement', value: '–40%', width: 40 },
      ],
    },
  };

  const testimonials = [
    {
      stars: 5,
      quote: "Before Datoula, we were sending the same generic emails to hundreds of prospects and seeing a 2–3% reply rate. Within 30 days, our reply rate jumped to over 11%. The AI summaries alone are worth the subscription — our reps now walk into every call knowing exactly who they're talking to.",
      initials: 'JH',
      name: 'James Harrington',
      role: 'Head of Sales Development · RevAgent Technologies',
      avatar: 'bg-blue-500',
    },
    {
      stars: 5,
      quote: 'We were spending nearly two hours per account on pre-call research. With Datoula, that\'s now under five minutes. The LinkedIn intelligence is extraordinary — it surfaces context I never would have found manually. Our discovery call quality has improved significantly, and deals are closing faster.',
      initials: 'PN',
      name: 'Priya Nambiar',
      role: 'Senior Account Executive · Stackhire SaaS',
      avatar: 'bg-teal-500',
    },
    {
      stars: 5,
      quote: 'As a startup founder doing outbound myself, Datoula has been a genuine game-changer. I can personalise outreach to 50 prospects a day with the same quality I used to put into 5. The AI summaries are sharp, accurate, and give me real conversation starters. Our booked meeting rate has tripled.',
      initials: 'MO',
      name: 'Marcus Obi',
      role: 'Co-Founder & CEO · GrowthOS',
      avatar: 'bg-violet-500',
    },
  ];

  return (
    <div className="bg-white text-gray-900 font-sans overflow-x-hidden py-20">

      {/* HERO */}
      <section className=" flex flex-col items-center justify-center text-center px-6 pt-20 relative overflow-hidden">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-full h-96 bg-gradient-radial from-blue-400/15 via-transparent to-transparent pointer-events-none rounded-full blur-3xl" />
        
        <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 text-blue-600 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider mb-8">
          <span className="inline-block w-2 h-2 bg-blue-600 rounded-full animate-pulse" />
          AI Sales Intelligence 
        </div>
        
        <h1 className="text-3xl md:text-4xl lg:text-5xl  font-black leading-tight tracking-tight text-gray-900 mb-6 max-w-4xl">
          Know Your Lead<br />
          <span className="text-blue-500">Before</span> You <span className="text-teal-500">Call.</span>
        </h1>
        
        <p className="text-lg text-gray-600 max-w-2xl mb-8 leading-relaxed">
          Datoula gives your sales team AI-powered lead profiles in seconds — combining LinkedIn data, social signals, and buying intent into one clear briefing, before you ever pick up the phone.
        </p>
        
        {/* <div className="flex gap-4 mb-16 justify-center flex-wrap">
          <button className="px-8 py-4 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-semibold text-lg transition transform hover:-translate-y-0.5 shadow-lg hover:shadow-xl">
            Book a Free Demo →
          </button>
          <button className="px-8 py-4 rounded-xl border-2 border-gray-300 hover:border-blue-500 text-gray-700 hover:text-blue-600 font-semibold text-lg transition">
            Start Free Trial — No Card Needed
          </button>
        </div> */}
        
        <div className="flex gap-12 justify-center flex-wrap border-t border-gray-300 pt-12 max-w-4xl">
          <div className="text-center">
            <div className="text-4xl font-black text-blue-500 mb-2">3×</div>
            <div className="text-sm text-gray-600">Higher Response Rates</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-black text-teal-500 mb-2">67%</div>
            <div className="text-sm text-gray-600">Less Research Time</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-black text-amber-500 mb-2">40%</div>
            <div className="text-sm text-gray-600">Increase in Pipeline</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-black text-gray-900 mb-2">500+</div>
            <div className="text-sm text-gray-600">Teams Onboarded</div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <div className="py-8 border-b border-gray-300 text-center bg-gray-50 mt-4">
        <p className="text-xs uppercase tracking-widest text-gray-500 mb-6 font-bold">Trusted by fast-growing teams worldwide</p>
        <div className="flex gap-12 justify-center flex-wrap">
          {['SalesTech', 'RevAgent', 'PitchLab', 'StackHire', 'GrowthOS', 'NexusB2B'].map((company) => (
            <span key={company} className="font-bold text-gray-500 text-sm uppercase tracking-wide">{company}</span>
          ))}
        </div>
      </div>

      {/* PROBLEM */}
      <section className="py-16 px-6 md:px-10 lg:px-28 bg-gray-50">
        <p className="text-xs uppercase tracking-widest text-blue-600 font-bold mb-4">The Problem</p>
        <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4 leading-tight">Sales Teams Are Pitching Blind.</h2>
        <p className="text-gray-600 max-w-2xl mb-12">The modern buyer researches everything before you call. Yet most reps walk in armed with only a name, a job title, and a scripted template — and wonder why deals stall.</p>
        
        <div className="grid md:grid-cols-2 gap-6">
          {[
            { title: 'Generic Outreach Gets Deleted', desc: 'When every message sounds identical, buyers switch off. Spray-and-pray tactics fail in a world where prospects receive dozens of cold messages daily. Without true personalisation, your emails go unread.' },
            { title: '45 Minutes Lost Per Lead on Research', desc: 'Sales reps spend up to 40% of their working day manually scrolling LinkedIn, reading company blogs, and piecing together context that should take seconds — not hours. That\'s time robbed directly from selling.' },
            { title: 'Low Conversions Kill Revenue', desc: 'Poorly informed pitches lead to poor outcomes. When you can\'t speak to a lead\'s specific priorities, pain points, or buying intent, deals stall before they start. Pipelines shrink. Targets get missed.' },
            { title: 'You Know Their Title — Not Their Story', desc: 'Knowing someone\'s job title is not the same as understanding them. Sales teams lack the deep insight into buying signals, professional interests, and decision-making patterns that make a conversation actually land.' },
          ].map((problem, idx) => (
            <div key={idx} className="bg-white border border-gray-200 rounded-2xl p-7 relative hover:shadow-md transition">
              <div className="absolute top-6 right-6 text-lg font-bold text-red-500">✕</div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">{problem.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{problem.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-16 px-6 md:px-10 lg:px-28 bg-white">
        <p className="text-xs uppercase tracking-widest text-blue-600 font-bold mb-4">How It Works</p>
        <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4 leading-tight">From Lead to Deal<br />in 5 Simple Steps.</h2>
        <p className="text-gray-600 max-w-2xl mb-16">No complex setup. No steep learning curve. Just instant intelligence, ready the moment your team needs it.</p>
        
        <div className='flex gap-10 '>
        <div className="max-w-3xl relative">
          {/* Vertical line */}
          <div className="absolute left-7 top-24 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 to-teal-500" />
          
          {[
            { num: '1', title: 'Import Your Leads', desc: 'Upload leads directly from your CRM, a CSV file, or via Datoula\'s native integrations with Salesforce, HubSpot, and Pipedrive. You can also look up individual leads instantly — just enter a name, email, or LinkedIn URL.' },
            { num: '2', title: 'AI Analyses Public Data', desc: 'Datoula\'s AI engine immediately scans publicly available data across LinkedIn, Twitter/X, company websites, news mentions, and professional directories — building a comprehensive data picture of your lead in real time.' },
            { num: '3', title: 'Generate Customer Insights', desc: 'Within 15–30 seconds, Datoula synthesises everything into a structured insight report: professional history, an interest map, communication style preferences, and active buying intent signals.' },
            { num: '4', title: 'Personalise Your Outreach', desc: 'Use AI-suggested personalisation hooks to craft messages that speak directly to the lead\'s priorities and context. Reference a recent career milestone, a post they published, or a shared professional interest — and open with instant credibility.' },
            { num: '5', title: 'Close Better Deals', desc: 'Walk into every call, demo, or pitch fully briefed. Build genuine rapport from the first second, handle objections with context, and close with the confidence of someone who already knows the room.' },
          ].map((step, idx) => (
            <div key={idx} className="flex gap-7 py-6">
              <div className="flex-shrink-0 relative z-10">
                <div className="w-14 h-14 rounded-full bg-white border-2 border-blue-500 flex items-center justify-center font-black text-lg text-blue-500 relative z-20 shadow-md">
                  {step.num}
                </div>
              </div>
              <div className="pt-2">
                <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed max-w-md">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div>
            <img src={image1} alt="Lead Story"  className='w-full h-auto object-cover'/>
        </div>
        </div>
      </section>

      {/* DASHBOARD MOCKUP */}
      <section className="py-12 px-6 md:px-10 lg:px-28 bg-gray-50">
        <p className="text-xs uppercase tracking-widest text-blue-600 font-bold mb-4 text-center">AI Dashboard</p>
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-relaxed text-center">Your Intelligence<br />Command Centre.</h2>
        <p className="text-gray-600 max-w-2xl mx-auto mb-12 text-center">Everything your team needs — lead scores, AI summaries, intent signals, and CRM sync — in one clean, intuitive interface.</p>
        
        <div className="border border-gray-300 rounded-3xl overflow-hidden bg-white shadow-xl">
          {/* Top bar */}
          <div className="bg-gray-100 border-b border-gray-300 px-6 py-4 flex items-center gap-3">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <div className="w-3 h-3 rounded-full bg-amber-500" />
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span className="ml-4 text-xs text-gray-600">Datoula — AI Intelligence Dashboard</span>
          </div>
          
          {/* Main content */}
          <div className="grid md:grid-cols-[220px_1fr] ">
            {/* Sidebar */}
            <div className="border-r border-gray-300 p-6 hidden md:flex flex-col gap-1">
              <div className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-4">Main</div>
              {['⚡ Dashboard', '👤 My Leads', '🔍 Lead Lookup', '📊 Analytics'].map((item, idx) => (
                <div key={idx} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm cursor-pointer ${idx === 0 ? 'bg-blue-500/10 text-blue-600 font-medium' : 'text-gray-600'}`}>
                  {item}
                </div>
              ))}
              <div className="text-xs uppercase tracking-widest text-gray-500 font-bold mt-4 mb-3">Integrations</div>
              {['🔗 CRM Sync', '📤 Outreach Tools'].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-600 cursor-pointer">
                  {item}
                </div>
              ))}
              <div className="text-xs uppercase tracking-widest text-gray-500 font-bold mt-4 mb-3">Team</div>
              {['👥 Team Performance', '⚙️ Settings'].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-600 cursor-pointer">
                  {item}
                </div>
              ))}
            </div>
            
            {/* Main content */}
            <div className="p-7">
              {/* KPI Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {[
                  { title: 'Leads Analysed Today', value: '142', color: 'text-blue-500', change: '↑ 18% vs yesterday' },
                  { title: 'Avg. Research Time', value: '28s', color: 'text-teal-500', change: '↓ 94% faster than manual' },
                  { title: 'High-Intent Leads', value: '37', color: 'text-amber-500', change: '↑ 12 new this week' },
                  { title: 'Reply Rate', value: '11.4%', color: 'text-violet-600', change: '↑ from 2.8% before' },
                ].map((kpi, idx) => (
                  <div key={idx} className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                    <div className="text-xs text-gray-600 mb-2">{kpi.title}</div>
                    <div className={`text-2xl font-black mb-1 ${kpi.color}`}>{kpi.value}</div>
                    <div className="text-xs text-green-600">{kpi.change}</div>
                  </div>
                ))}
              </div>
              
              {/* Leads Table */}
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="border-b border-gray-200 px-5 py-3 flex justify-between items-center">
                  <h4 className="font-bold text-gray-900 text-sm">Recent Lead Profiles</h4>
                  <span className="bg-blue-500/10 text-blue-600 text-xs font-bold px-3 py-1 rounded-full">5 new</span>
                </div>
                
                <div className="bg-gray-50 px-5 py-2 text-xs text-gray-600 font-bold uppercase tracking-wider flex justify-between">
                  <div>Lead</div>
                  <div>AI Score</div>
                  <div>Buying Intent</div>
                  <div>Source</div>
                  <div>AI Briefing</div>
                </div>
                
                {[
                  { name: 'Sarah Okonkwo', company: 'VP Sales · Nexus B2B', score: 92, intent: '🔥 Hot', source: 'LinkedIn Import' },
                  { name: 'Marcus Chen', company: 'Head of Growth · SeedStack', score: 74, intent: '⚡ Warm', source: 'HubSpot Sync' },
                  { name: 'Priya Nambiar', company: 'Account Executive · StackHire', score: 88, intent: '🔥 Hot', source: 'CSV Import' },
                  { name: 'James Obi', company: 'Co-Founder · GrowthOS', score: 61, intent: '❄️ Researching', source: 'Manual Entry' },
                ].map((lead, idx) => (
                  <div key={idx} className="border-b border-gray-200 last:border-b-0 px-5 py-3 flex justify-between items-center text-xs hover:bg-gray-50 transition">
                    <div>
                      <div className="font-semibold text-gray-900">{lead.name}</div>
                      <div className="text-gray-600 text-xs mt-1">{lead.company}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-1 w-16 bg-gray-300 rounded overflow-hidden">
                        <div className={`h-full ${lead.score > 80 ? 'bg-green-500' : 'bg-amber-500'}`} style={{width: `${lead.score}%`}} />
                      </div>
                      <span className="font-bold text-gray-900">{lead.score}</span>
                    </div>
                    <div>{lead.intent}</div>
                    <div className="text-gray-600">{lead.source}</div>
                    <button className="text-blue-500 hover:text-blue-600 font-medium">View Summary</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-16 px-6 md:px-10 lg:px-28 bg-white" id="features">
        <p className="text-xs uppercase tracking-widest text-blue-600 font-bold mb-4 text-center">Features</p>
        <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4 leading-tight text-center">Powerful Intelligence,<br />Built for Sales.</h2>
        <p className="text-gray-600 max-w-2xl mx-auto mb-16 text-center">Every feature exists for one reason: to make sure your team walks into every conversation knowing more than the competition.</p>
        
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: '🔗', title: 'Deep LinkedIn Intelligence', desc: 'Go far beyond the profile. Datoula analyses a prospect\'s career history, job transitions, skills, endorsements, activity patterns, and connections — building a complete professional picture you can act on before the first call.' },
            { icon: '📡', title: 'Real-Time Buying Signals', desc: 'Identify leads who are actively researching solutions in your category, engaging with competitor content, or signalling purchase intent — so you can time your outreach for maximum impact, not after they\'ve already decided.' },
            { icon: '⚡', title: 'AI Lead Summaries', desc: 'In seconds, Datoula generates a clean, human-readable one-paragraph briefing for every lead — combining professional history, interests, communication style, and buying intent into a single briefing your rep can read in 30 seconds.' },
            { icon: '🎯', title: 'Smart Personalisation at Scale', desc: 'Datoula automatically surfaces personalisation hooks — a lead\'s recent post, a career milestone, a shared interest — so every outreach message feels hand-crafted, even when you\'re reaching out to hundreds of prospects a week.' },
            { icon: '📱', title: 'Social Media Analysis', desc: 'Datoula scans a lead\'s public Twitter/X, LinkedIn, and other social presences to surface professional interests, tone, trending topics, and priorities — making personalisation feel effortless and authentic, not researched.' },
            { icon: '🔌', title: 'CRM & Stack Integration', desc: 'Insights sync directly to Salesforce, HubSpot, Pipedrive, and Zoho. Your team never has to leave their existing workflow. Datoula slots into how you already work — no disruption, no retraining, no steep learning curve.' },
          ].map((feature, idx) => (
            <div key={idx} className="bg-white border border-gray-200 rounded-2xl p-7 hover:border-blue-500 hover:shadow-lg transition group">
              <div className="text-4xl mb-4">{feature.icon}</div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">{feature.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{feature.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-16 px-6 md:px-10 lg:px-28 bg-gray-50" id="industries">
        <p className="text-xs uppercase tracking-widest text-blue-600 font-bold mb-4">Industries</p>
        <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4 leading-tight">Built for Every Team<br />That Sells.</h2>
        <p className="text-gray-600 max-w-2xl mb-12">Whether you're an SDR hitting daily prospecting targets or a founder closing enterprise deals, Datoula gives you the intelligence edge that changes outcomes — across every industry.</p>
        
        {/* Tabs */}
        <div className="flex gap-2 flex-wrap mb-12">
          {[
            { id: 'b2b', label: 'B2B Sales Teams' },
            { id: 'sdr', label: 'SDR & BDR Teams' },
            { id: 'saas', label: 'SaaS Startups' },
            { id: 'abm', label: 'Marketing & ABM' },
            { id: 'recruit', label: 'Recruitment' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-full border text-sm font-medium transition ${
                activeTab === tab.id
                  ? 'bg-blue-500 border-blue-500 text-white shadow-md'
                  : 'border-gray-300 text-gray-700 hover:border-gray-400'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        
        {/* Tab Content */}
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-3xl font-black text-gray-900 mb-4 leading-tight">{industryData[activeTab].title}</h3>
            <p className="text-gray-600 mb-6 leading-relaxed">{industryData[activeTab].description}</p>
            <ul className="space-y-3">
              {industryData[activeTab].wins.map((win, idx) => (
                <li key={idx} className="flex gap-3 text-sm">
                  <span className="text-teal-500 font-bold flex-shrink-0">✓</span>
                  <span className="text-gray-700">{win}</span>
                </li>
              ))}
            </ul>
          </div>
          
          <div className="bg-white border border-gray-200 rounded-2xl p-8">
            <h4 className="text-sm font-bold text-gray-600 uppercase tracking-wider mb-6">Performance Uplift</h4>
            <div className="space-y-6">
              {industryData[activeTab].metrics.map((metric, idx) => (
                <div key={idx}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs text-gray-600 font-medium">{metric.label}</span>
                    <span className="text-sm font-bold text-gray-900">{metric.value}</span>
                  </div>
                  <div className="h-1 bg-gray-300 rounded overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blue-500 to-teal-500"
                      style={{ width: `${metric.width}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16 px-6 md:px-10 lg:px-28 bg-white" id="testimonials">
        <p className="text-xs uppercase tracking-widest text-blue-600 font-bold mb-4 text-center">Testimonials</p>
        <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4 leading-tight text-center">Real Results From<br />Real Sales Teams.</h2>
        <p className="text-gray-600 max-w-2xl mx-auto mb-16 text-center">Don't take our word for it. Here's what sales professionals using Datoula say about the difference it's made to their pipeline.</p>
        
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((testi, idx) => (
            <div key={idx} className="bg-gray-50 border border-gray-200 rounded-2xl p-7 hover:shadow-md transition">
              <div className="text-amber-500 text-sm mb-4">{'★'.repeat(testi.stars)}</div>
              <p className="text-gray-700 italic mb-6 leading-relaxed text-sm">{testi.quote}</p>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full ${testi.avatar} flex items-center justify-center text-white font-bold text-sm`}>
                  {testi.initials}
                </div>
                <div>
                  <div className="text-gray-900 font-bold text-sm">{testi.name}</div>
                  <div className="text-gray-600 text-xs">{testi.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-gray-50" id="faq">
        <p className="text-xs uppercase tracking-widest text-blue-600 font-bold mb-4 text-center">FAQ</p>
        <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4 leading-tight text-center">Frequently Asked Questions.</h2>
        <p className="text-gray-600 max-w-2xl mx-auto mb-16 text-center">Have a question about Datoula? We've probably answered it below.</p>
        
        <div className="max-w-2xl mx-auto">
          {faqItems.map((item, idx) => (
            <div key={idx} className="border-b border-gray-300 py-5">
              <button
                onClick={() => toggleFaq(idx)}
                className="w-full text-left flex justify-between items-center gap-4 font-semibold text-gray-900 hover:text-blue-600 transition"
              >
                <span>{item.question}</span>
                <span className={`text-blue-500 font-bold text-xl transition-transform ${openFaq === idx ? 'rotate-45' : ''}`}>+</span>
              </button>
              {openFaq === idx && (
                <p className="text-gray-600 mt-4 text-sm leading-relaxed">{item.answer}</p>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 md:px-10 lg:px-28 text-center bg-gradient-to-b from-blue-500/5 to-transparent border-t border-gray-300">
        <p className="text-xs uppercase tracking-widest text-blue-600 font-bold mb-4">Ready?</p>
        <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4 leading-tight">Know Every Lead<br />Before You Call.</h2>
        <p className="text-gray-600 max-w-2xl mx-auto mb-8">Join 500+ sales teams already using Datoula to personalise outreach, close more deals, and build pipeline faster with AI-powered intelligence.</p>
        
        <div className="flex gap-4 justify-center flex-wrap mb-6">
          <button className="px-8 py-4 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-semibold text-lg transition transform hover:-translate-y-0.5 shadow-lg hover:shadow-xl">
            Book a Free Demo →
          </button>
          <button className="px-8 py-4 rounded-xl border-2 border-gray-300 hover:border-blue-500 text-gray-700 hover:text-blue-600 font-semibold text-lg transition">
            Start Free Trial
          </button>
        </div>
        <p className="text-xs text-gray-500">No credit card required · 14-day free trial · Cancel anytime · GDPR compliant</p>
      </section>
    </div>
  );
}